import React, { useState, useMemo, useEffect, useRef } from "react";
import { Box, Stack, Typography, Chip, Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DescriptionIcon from "@mui/icons-material/Description";
import HistoryIcon from "@mui/icons-material/History";
import { CustomSectionTitle, CustomLoading } from "@ippon/shared-components";
import { REVISION_FIELD_LABELS } from "../types";
import type { PrivateAreaHistoryRecord } from "../types";
import PreviousContractorModal from "./PreviousContractorModal";

const formatRevisionValue = (field: string, value: unknown): string => {
  if (value === undefined || value === null || value === "") return "";
  if (field === "pricing_type") {
    return value === "unit_quantity" ? "単価 × 数量" : "固定月額";
  }
  if (field === "unit_price") {
    const amount = Number(value);
    return Number.isNaN(amount) ? String(value) : `¥${amount.toLocaleString()}`;
  }
  return String(value);
};

interface ContractHistoryTabProps {
  history: PrivateAreaHistoryRecord[];
  expandedOthers: Record<string, boolean>;
  setExpandedOthers: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  expandedEditHistory: Record<string, boolean>;
  setExpandedEditHistory: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  expandedRevisionItems: Record<string, boolean>;
  setExpandedRevisionItems: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  handlePreviewDocument: (s3Key: string, fileName: string) => void;
  /** 退去レコードの「契約情報を見る」ボタン用に Unit ID を渡す */
  unitId?: string;
  /** 無限スクロール: 次ページ取得トリガ (末尾 sentinel が viewport に入ったら呼ぶ) */
  onLoadMore?: () => void;
  /** これ以上ページがあるか */
  hasMore?: boolean;
  /** 初回取得中フラグ */
  loading?: boolean;
  /** 追加取得中フラグ */
  isLoadingMore?: boolean;
}

const ContractHistoryTab: React.FC<ContractHistoryTabProps> = ({
  history,
  expandedOthers,
  setExpandedOthers,
  expandedEditHistory,
  setExpandedEditHistory,
  expandedRevisionItems,
  setExpandedRevisionItems,
  handlePreviewDocument,
  unitId,
  onLoadMore,
  hasMore = false,
  loading = false,
  isLoadingMore = false,
}) => {
  // 「契約情報を見る」モーダル: 開いている退去レコード ID を保持。
  const [previousContractorModalRecordId, setPreviousContractorModalRecordId] =
    useState<string | null>(null);
  const previousContractorModalRecord = useMemo(
    () =>
      previousContractorModalRecordId
        ? (history || []).find(
            (r) => r.id === previousContractorModalRecordId,
          ) || null
        : null,
    [previousContractorModalRecordId, history],
  );

  // 無限スクロール: 末尾 sentinel が viewport に入ったら onLoadMore
  const bottomObserverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!onLoadMore) return;
    const target = bottomObserverRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(target);
    return () => {
      observer.unobserve(target);
    };
  }, [onLoadMore, hasMore, isLoadingMore]);

  const pagedHistory = history || [];

  return (
    <Box sx={{ mt: 2, pb: 3 }}>
      {/* タイトルは全幅で出す (addBorder の下線をきれいに引くため横並び要素は同列に置かない) */}
      <Box sx={{ mb: 2 }}>
        <CustomSectionTitle title="契約履歴" addBorder />
      </Box>
      <Box
        sx={{
          background: "#fff",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 240,
              width: "100%",
            }}
          >
            <CustomLoading loading={true} fullScreen={false} size="medium" />
          </Box>
        ) : pagedHistory.length > 0 ? (
          <Stack spacing={2}>
            {pagedHistory.map((record, idx) => {
              const dotColor =
                record.type === "created"
                  ? "#188D1C"
                  : record.type === "document_delete"
                    ? "#6B7280"
                    : record.type === "contract_end"
                      ? "#ED6C02"
                      : "#1976D2";

              const chipStyle =
                record.type === "created"
                  ? {
                      textColor: "#188D1C",
                      bgColor: "rgba(169, 222, 171, 0.4)",
                      borderColor: "#A9DEAB",
                    }
                  : record.type === "document_delete"
                    ? {
                        textColor: "#6B7280",
                        bgColor: "rgba(156, 163, 175, 0.2)",
                        borderColor: "#D1D5DB",
                      }
                    : record.type === "contract_end"
                      ? {
                          textColor: "#ED6C02",
                          bgColor: "rgba(255, 152, 0, 0.2)",
                          borderColor: "#FFB74D",
                        }
                      : {
                          textColor: "#1976D2",
                          bgColor: "rgba(144, 202, 249, 0.4)",
                          borderColor: "#90CAF9",
                        };

              const isNewContract = record.type === "created";

              return (
                <Stack key={record.id} direction="row" spacing={1.5}>
                  {/* タイムラインアイコン */}
                  <Stack alignItems="center">
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: dotColor,
                        mt: "7px",
                      }}
                    />
                    {idx < history.length - 1 && (
                      <Box
                        sx={{
                          width: 2,
                          flex: 1,
                          background: "#e5e7eb",
                          mt: 0.5,
                        }}
                      />
                    )}
                  </Stack>

                  {/* 履歴内容 */}
                  <Box flex={1} pb={2}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      mb={0.5}
                      sx={{ minHeight: 24 }}
                    >
                      <Chip
                        size="small"
                        label={
                          record.type === "created"
                            ? "新規契約"
                            : record.type === "renewal"
                              ? "契約更新"
                              : record.type === "rent_change"
                                ? "賃料更新"
                                : record.type === "tenant_change"
                                  ? "契約者更新"
                                  : record.type === "document_upload"
                                    ? "書類追加"
                                    : record.type === "document_delete"
                                      ? "書類削除"
                                      : record.type === "contract_end"
                                        ? "契約終了"
                                        : "その他"
                        }
                        sx={{
                          backgroundColor: chipStyle.bgColor,
                          color: chipStyle.textColor,
                          border: `1px solid ${chipStyle.borderColor}`,
                          fontSize: "14px",
                          fontWeight: 600,
                          borderRadius: "16px",
                          cursor: "default",
                          "&:hover": {
                            backgroundColor: chipStyle.bgColor,
                            opacity: 1,
                          },
                          "&:focus": { backgroundColor: chipStyle.bgColor },
                        }}
                      />
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography fontSize={12} color="#6b7280">
                          {record.date}
                        </Typography>
                        {record.isEdited && (
                          <Typography fontSize={12} color="#6b7280">
                            （編集済み）
                          </Typography>
                        )}
                      </Stack>
                    </Stack>

                    <Box
                      sx={{ background: "#f9fafb", borderRadius: "8px", p: 2 }}
                    >
                      {isNewContract ? (
                        <>
                          <Typography fontSize={14} fontWeight={700} mb={1.5}>
                            契約内容
                          </Typography>
                          <Stack spacing={0.5} sx={{ pl: 1.5 }}>
                            {[
                              {
                                label: "契約者氏名",
                                value: record.contractDetails?.tenantName,
                              },
                              {
                                label: "電話番号",
                                value: record.contractDetails?.tenantPhone,
                              },
                              {
                                label: "メールアドレス",
                                value: record.contractDetails?.tenantEmail,
                              },
                              {
                                label: "契約形態",
                                value: record.contractDetails?.contractForm,
                              },
                              {
                                label: "契約開始日",
                                value: record.contractDetails?.startDate,
                              },
                              {
                                label: "契約終了日",
                                value: record.contractDetails?.endDate,
                              },
                              {
                                label: "賃料起算日",
                                value: record.contractDetails?.rentStartDate,
                              },
                              {
                                label: "解約予定日",
                                value: record.contractDetails?.cancellationDate,
                              },
                              {
                                label: "月額賃料（税別）",
                                value: record.contractDetails?.monthlyRent
                                  ? `¥${Number(record.contractDetails.monthlyRent).toLocaleString()}`
                                  : undefined,
                              },
                              {
                                label: "料金体系",
                                value:
                                  record.contractDetails?.pricingType ===
                                  "unit_quantity"
                                    ? "単価 × 数量"
                                    : record.contractDetails?.pricingType ===
                                        "fixed"
                                      ? "固定月額"
                                      : undefined,
                              },
                              {
                                label: "単価",
                                value:
                                  record.contractDetails?.pricingType ===
                                    "unit_quantity" &&
                                  record.contractDetails?.unitPrice !== undefined
                                    ? `¥${Number(record.contractDetails.unitPrice).toLocaleString()}`
                                    : undefined,
                              },
                              {
                                label: "数量",
                                value:
                                  record.contractDetails?.pricingType ===
                                    "unit_quantity" &&
                                  record.contractDetails?.quantity !== undefined
                                    ? `${record.contractDetails.quantity}${record.contractDetails.quantityUnit || ""}`
                                    : undefined,
                              },
                              {
                                label: "共益費",
                                value: record.contractDetails?.maintenanceFee
                                  ? `¥${Number(record.contractDetails.maintenanceFee).toLocaleString()}`
                                  : undefined,
                              },
                              {
                                label: "敷金",
                                value: record.contractDetails?.deposit
                                  ? `¥${Number(record.contractDetails.deposit).toLocaleString()}`
                                  : undefined,
                              },
                              {
                                label: "償却後敷金",
                                value: record.contractDetails?.depositAfter
                                  ? `¥${Number(record.contractDetails.depositAfter).toLocaleString()}`
                                  : undefined,
                              },
                              {
                                label: "礼金",
                                value: record.contractDetails?.keyMoney
                                  ? `¥${Number(record.contractDetails.keyMoney).toLocaleString()}`
                                  : undefined,
                              },
                              {
                                label: "備考",
                                value: record.contractDetails?.remarks,
                              },
                            ].map(({ label, value }) => (
                              <Stack
                                key={label}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography fontSize={12} color="#6b7280">
                                  {label}:
                                </Typography>
                                <Typography fontSize={12} fontWeight={700}>
                                  {value || "-"}
                                </Typography>
                              </Stack>
                            ))}
                            {record.contractDetails?.otherFees &&
                              record.contractDetails.otherFees.length > 0 && (
                                <>
                                  <Typography
                                    fontSize={12}
                                    color="#6b7280"
                                    mt={0.5}
                                  >
                                    その他料金:
                                  </Typography>
                                  {record.contractDetails.otherFees.map(
                                    (fee, feeIdx) => (
                                      <Stack
                                        key={feeIdx}
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        sx={{ pl: 1 }}
                                      >
                                        <Typography
                                          fontSize={12}
                                          color="#6b7280"
                                        >
                                          {fee.label}:
                                        </Typography>
                                        <Typography
                                          fontSize={12}
                                          fontWeight={700}
                                        >{`¥${Number(fee.amount).toLocaleString()}`}</Typography>
                                      </Stack>
                                    ),
                                  )}
                                </>
                              )}
                          </Stack>
                        </>
                      ) : record.type === "document_upload" ? (
                        <>
                          <Typography fontSize={14} fontWeight={700} mb={1.5}>
                            追加内容
                          </Typography>
                          <Stack spacing={0.5}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography fontSize={12} color="#6b7280">
                                契約者名:
                              </Typography>
                              <Typography fontSize={12} fontWeight={700}>
                                {record.tenantName || "-"}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                              <Typography
                                fontSize={12}
                                color="#6b7280"
                                sx={{ flexShrink: 0 }}
                              >
                                書類:
                              </Typography>
                              <Stack spacing={0.5} flex={1}>
                                {record.uploadedDocuments &&
                                record.uploadedDocuments.length > 0 ? (
                                  record.uploadedDocuments.map(
                                    (doc, docIdx) => (
                                      <Stack
                                        key={docIdx}
                                        direction="row"
                                        alignItems="center"
                                        spacing={0.5}
                                        sx={{
                                          cursor: "pointer",
                                          "&:hover": {
                                            textDecoration: "underline",
                                          },
                                        }}
                                        onClick={() =>
                                          handlePreviewDocument(
                                            doc.s3_key,
                                            doc.fileName.replace(
                                              /^\d+_\d+_/,
                                              "",
                                            ),
                                          )
                                        }
                                      >
                                        <DescriptionIcon
                                          sx={{
                                            fontSize: 16,
                                            color: "#6B7280",
                                          }}
                                        />
                                        <Typography
                                          fontSize={12}
                                          fontWeight={700}
                                          color="#3e3e3e"
                                        >
                                          {doc.fileName.replace(
                                            /^\d+_\d+_/,
                                            "",
                                          )}
                                        </Typography>
                                      </Stack>
                                    ),
                                  )
                                ) : (
                                  <Typography
                                    fontSize={12}
                                    fontWeight={700}
                                    color="#3e3e3e"
                                  >
                                    -
                                  </Typography>
                                )}
                              </Stack>
                            </Stack>
                          </Stack>
                        </>
                      ) : record.type === "document_delete" ? (
                        <>
                          <Typography fontSize={14} fontWeight={700} mb={1.5}>
                            削除内容
                          </Typography>
                          <Stack spacing={0.5}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography fontSize={12} color="#6b7280">
                                契約者名:
                              </Typography>
                              <Typography fontSize={12} fontWeight={700}>
                                {record.tenantName || "-"}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                              <Typography
                                fontSize={12}
                                color="#6b7280"
                                sx={{ flexShrink: 0 }}
                              >
                                書類:
                              </Typography>
                              <Stack spacing={0.5} flex={1}>
                                {record.deletedDocuments &&
                                record.deletedDocuments.length > 0 ? (
                                  record.deletedDocuments.map((doc, docIdx) => (
                                    <Stack
                                      key={docIdx}
                                      direction="row"
                                      alignItems="center"
                                      spacing={0.5}
                                      sx={{
                                        cursor: "pointer",
                                        "&:hover": {
                                          textDecoration: "underline",
                                        },
                                      }}
                                      onClick={() =>
                                        handlePreviewDocument(
                                          doc.s3_key,
                                          doc.fileName.replace(/^\d+_\d+_/, ""),
                                        )
                                      }
                                    >
                                      <DescriptionIcon
                                        sx={{ fontSize: 16, color: "#6B7280" }}
                                      />
                                      <Typography
                                        fontSize={12}
                                        fontWeight={700}
                                        color="#3e3e3e"
                                      >
                                        {doc.fileName.replace(/^\d+_\d+_/, "")}
                                      </Typography>
                                    </Stack>
                                  ))
                                ) : (
                                  <Typography fontSize={12}>なし</Typography>
                                )}
                              </Stack>
                            </Stack>
                          </Stack>
                        </>
                      ) : record.type === "contract_end" ? (
                        <Box
                          sx={{
                            borderRadius: "8px",
                            background:
                              "linear-gradient(90deg, #FEF2F2 0%, #FFF1F2 100%)",
                            border: "1px solid #FECACA",
                            p: 1,
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ color: "#B91C1C" }}
                          >
                            <Typography fontSize={14} fontWeight={700}>
                              契約終了
                            </Typography>
                          </Stack>

                          <Box
                            sx={{
                              mt: 1,
                              p: 1,
                              backgroundColor: "rgba(255,255,255,0.7)",
                              border: "1px solid #FEE2E2",
                              borderRadius: "8px",
                            }}
                          >
                            <Typography fontSize={12} color="#6b7280" mb={0.5}>
                              退去契約者
                            </Typography>
                            <Typography
                              fontSize={14}
                              fontWeight={700}
                              color="#1F2937"
                            >
                              {record.tenantName ||
                                record.contractDetails?.tenantName ||
                                "-"}
                            </Typography>
                            <Typography fontSize={12} color="#6b7280" mt={0.75}>
                              {(record.contractDetails?.startDate || "-") +
                                " 〜 " +
                                (record.departureDate ||
                                  record.contractDetails?.endDate ||
                                  "-")}
                            </Typography>
                          </Box>

                          {/* 退去理由はモーダル (PreviousContractorModal) 内の契約情報側で確認できるため、
                              履歴カードには出さない */}

                          {/* 契約情報を見るボタン */}
                          <Box
                            sx={{
                              mt: 1,
                              pt: 1,
                              borderTop: "1px solid #FECACA",
                            }}
                          >
                            {/* CustomButton "cancel" の !important スタイルが
                                color を強制グレー化するため、Box component="button" で直接組む */}
                            <Box
                              component="button"
                              type="button"
                              onClick={() =>
                                setPreviousContractorModalRecordId(record.id)
                              }
                              sx={{
                                width: "100%",
                                height: 34,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.75,
                                borderRadius: "10px",
                                border: "1px solid #FECACA",
                                backgroundColor: "#fff",
                                color: "#B91C1C",
                                fontSize: "14px",
                                fontWeight: 700,
                                fontFamily: '"Noto Sans JP", sans-serif',
                                cursor: "pointer",
                                padding: "0 8px",
                                "&:hover": {
                                  backgroundColor: "#FEF2F2",
                                  borderColor: "#FCA5A5",
                                },
                              }}
                            >
                              <HistoryIcon
                                sx={{ fontSize: 16, color: "#B91C1C" }}
                              />
                              {`${record.tenantName || record.contractDetails?.tenantName || "前契約者"}の契約情報を見る`}
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <>
                          {record.changes && record.changes.length > 0 && (
                            <>
                              <Typography
                                fontSize={14}
                                fontWeight={700}
                                mb={1.5}
                              >
                                更新内容
                              </Typography>
                              <Stack spacing={0.5} sx={{ pl: 1.5 }}>
                                {record.changes.map((c, i) => (
                                  <Stack
                                    key={i}
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <Typography fontSize={12} color="#6b7280">
                                      {c.field}:
                                    </Typography>
                                    {c.oldValue && c.newValue ? (
                                      <>
                                        <Typography
                                          fontSize={12}
                                          color="#9ca3af"
                                          sx={{
                                            textDecoration: "line-through",
                                          }}
                                        >
                                          {c.oldValue}
                                        </Typography>
                                        <Typography fontSize={12}>→</Typography>
                                        <Typography
                                          fontSize={12}
                                          fontWeight={700}
                                        >
                                          {c.newValue}
                                        </Typography>
                                      </>
                                    ) : c.oldValue ? (
                                      <Typography
                                        fontSize={12}
                                        color="#9ca3af"
                                        sx={{ textDecoration: "line-through" }}
                                      >
                                        {c.oldValue}
                                      </Typography>
                                    ) : (
                                      <Typography
                                        fontSize={12}
                                        fontWeight={700}
                                      >
                                        {c.newValue}
                                      </Typography>
                                    )}
                                  </Stack>
                                ))}
                              </Stack>
                            </>
                          )}
                        </>
                      )}

                      {/* その他セクション（折りたたみ） */}
                      <Box sx={{ mt: 1.5 }}>
                        <Box
                          onClick={() =>
                            setExpandedOthers((prev) => ({
                              ...prev,
                              [record.id]: !prev[record.id],
                            }))
                          }
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                            ...(!record.revisionHistory?.length && {
                              borderTop: "1px solid #e5e7eb",
                              pt: 1.5,
                            }),
                            "&:hover": {
                              "& .MuiTypography-root": { color: "#3e3e3e" },
                            },
                          }}
                        >
                          {expandedOthers[record.id] ? (
                            <ExpandMoreIcon
                              sx={{ fontSize: 18, color: "#6b7280" }}
                            />
                          ) : (
                            <ChevronRightIcon
                              sx={{ fontSize: 18, color: "#6b7280" }}
                            />
                          )}
                          <Typography
                            fontSize={12}
                            fontWeight={700}
                            color="#6b7280"
                          >
                            その他
                          </Typography>
                        </Box>
                        <Collapse
                          in={!!expandedOthers[record.id]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Stack spacing={1} mt={1.5} sx={{ pl: 1.5 }}>
                            {isNewContract &&
                              record.uploadedDocuments &&
                              record.uploadedDocuments.length > 0 && (
                                <Box>
                                  <Typography
                                    fontSize={12}
                                    color="#6b7280"
                                    mb={0.5}
                                  >
                                    関連書類
                                  </Typography>
                                  <Stack spacing={0.5}>
                                    {record.uploadedDocuments.map(
                                      (doc, docIdx) => (
                                        <Stack
                                          key={docIdx}
                                          direction="row"
                                          alignItems="center"
                                          spacing={0.5}
                                          sx={{
                                            cursor: "pointer",
                                            "&:hover": {
                                              textDecoration: "underline",
                                            },
                                          }}
                                          onClick={() =>
                                            handlePreviewDocument(
                                              doc.s3_key,
                                              doc.fileName.replace(
                                                /^\d+_\d+_/,
                                                "",
                                              ),
                                            )
                                          }
                                        >
                                          <DescriptionIcon
                                            sx={{
                                              fontSize: 16,
                                              color: "#6B7280",
                                            }}
                                          />
                                          <Typography
                                            fontSize={12}
                                            color="#3e3e3e"
                                          >
                                            {doc.fileName.replace(
                                              /^\d+_\d+_/,
                                              "",
                                            )}
                                          </Typography>
                                        </Stack>
                                      ),
                                    )}
                                  </Stack>
                                </Box>
                              )}
                            {isNewContract ? (
                              <>
                                <Box>
                                  <Typography fontSize={12} color="#6b7280">
                                    作成者
                                  </Typography>
                                  <Typography fontSize={12} color="#3e3e3e">
                                    {record.createdBy || "-"}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography fontSize={12} color="#6b7280">
                                    作成日時
                                  </Typography>
                                  <Typography fontSize={12} color="#3e3e3e">
                                    {record.date}
                                  </Typography>
                                </Box>
                                {record.isEdited && (
                                  <>
                                    <Box>
                                      <Typography fontSize={12} color="#6b7280">
                                        更新者
                                      </Typography>
                                      <Typography fontSize={12} color="#3e3e3e">
                                        {record.updatedBy || "-"}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography fontSize={12} color="#6b7280">
                                        最終更新日時
                                      </Typography>
                                      <Typography fontSize={12} color="#3e3e3e">
                                        {record.lastUpdatedAt || "-"}
                                      </Typography>
                                    </Box>
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                <Box>
                                  <Typography fontSize={12} color="#6b7280">
                                    更新者
                                  </Typography>
                                  <Typography fontSize={12} color="#3e3e3e">
                                    {record.createdBy || "-"}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography fontSize={12} color="#6b7280">
                                    最終更新日時
                                  </Typography>
                                  <Typography fontSize={12} color="#3e3e3e">
                                    {record.lastUpdatedAt || record.date}
                                  </Typography>
                                </Box>
                              </>
                            )}
                          </Stack>
                        </Collapse>
                      </Box>

                      {/* 編集履歴セクション（折りたたみ） */}
                      {record.revisionHistory &&
                        record.revisionHistory.length > 0 && (
                          <Box
                            sx={{
                              mt: 1.5,
                              borderTop: "1px solid #e5e7eb",
                              pt: 1.5,
                            }}
                          >
                            <Box
                              onClick={() =>
                                setExpandedEditHistory((prev) => ({
                                  ...prev,
                                  [record.id]: !prev[record.id],
                                }))
                              }
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                cursor: "pointer",
                                userSelect: "none",
                              }}
                            >
                              {expandedEditHistory[record.id] ? (
                                <ExpandMoreIcon
                                  sx={{ fontSize: 18, color: "#374151" }}
                                />
                              ) : (
                                <ChevronRightIcon
                                  sx={{ fontSize: 18, color: "#374151" }}
                                />
                              )}
                              <AccessTimeIcon
                                sx={{ fontSize: 16, color: "#374151" }}
                              />
                              <Typography
                                fontSize={12}
                                fontWeight={500}
                                color="#374151"
                              >
                                編集履歴（{record.revisionHistory.length}件）
                              </Typography>
                            </Box>
                            <Collapse
                              in={!!expandedEditHistory[record.id]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ mt: 1.5, ml: 4 }}>
                                {[...(record.revisionHistory || [])]
                                  .reverse()
                                  .map((rev: any, revIdx: number) => {
                                    const changeEntries = Object.entries(
                                      rev.changes || {},
                                    ) as [
                                      string,
                                      { before: any; after: any },
                                    ][];
                                    const revKey = `${record.id}-rev-${revIdx}`;
                                    const isRevOpen =
                                      !!expandedRevisionItems[revKey];
                                    return (
                                      <Box
                                        key={revIdx}
                                        sx={{
                                          position: "relative",
                                          borderLeft: "2px solid #d1d5db",
                                          pl: 2,
                                          py: 1,
                                          "&::before": {
                                            content: '""',
                                            position: "absolute",
                                            left: -7,
                                            top: 12,
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            backgroundColor: "#9ca3af",
                                            border: "2px solid #fff",
                                            zIndex: 1,
                                          },
                                        }}
                                      >
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          spacing={1}
                                          onClick={() =>
                                            setExpandedRevisionItems(
                                              (prev) => ({
                                                ...prev,
                                                [revKey]: !prev[revKey],
                                              }),
                                            )
                                          }
                                          sx={{
                                            cursor: "pointer",
                                            userSelect: "none",
                                          }}
                                        >
                                          {isRevOpen ? (
                                            <ExpandMoreIcon
                                              sx={{
                                                fontSize: 16,
                                                color: "#6b7280",
                                              }}
                                            />
                                          ) : (
                                            <ChevronRightIcon
                                              sx={{
                                                fontSize: 16,
                                                color: "#6b7280",
                                              }}
                                            />
                                          )}
                                          <Typography
                                            fontSize={12}
                                            color="#374151"
                                            fontWeight={500}
                                          >
                                            編集
                                          </Typography>
                                          <Typography
                                            fontSize={12}
                                            color="#6b7280"
                                          >
                                            {new Date(
                                              rev.revised_at,
                                            ).toLocaleString("ja-JP")}
                                          </Typography>
                                          {rev.revised_by_name && (
                                            <Typography
                                              fontSize={12}
                                              sx={{
                                                backgroundColor: "#f3f4f6",
                                                color: "#374151",
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: "9999px",
                                              }}
                                            >
                                              {rev.revised_by_name}
                                            </Typography>
                                          )}
                                        </Stack>
                                        <Collapse
                                          in={isRevOpen}
                                          timeout="auto"
                                          unmountOnExit
                                        >
                                          <Box
                                            sx={{
                                              mt: 1,
                                              ml: 3,
                                              backgroundColor: "#fff",
                                              border: "1px solid #e5e7eb",
                                              borderRadius: 2,
                                              p: 1.5,
                                            }}
                                          >
                                            {changeEntries.map(
                                              ([field, val], cIdx) => {
                                                const label =
                                                  REVISION_FIELD_LABELS[
                                                    field
                                                  ] || field;
                                                const oldVal =
                                                  formatRevisionValue(
                                                    field,
                                                    val.before,
                                                  );
                                                const newVal =
                                                  formatRevisionValue(
                                                    field,
                                                    val.after,
                                                  );
                                                return (
                                                  <Stack
                                                    key={cIdx}
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1}
                                                    mb={0.5}
                                                  >
                                                    <Typography
                                                      fontSize={12}
                                                      color="#6b7280"
                                                    >
                                                      {label}:
                                                    </Typography>
                                                    {oldVal && newVal ? (
                                                      <>
                                                        <Typography
                                                          fontSize={12}
                                                          color="#9ca3af"
                                                          sx={{
                                                            textDecoration:
                                                              "line-through",
                                                          }}
                                                        >
                                                          {oldVal}
                                                        </Typography>
                                                        <Typography
                                                          fontSize={12}
                                                        >
                                                          →
                                                        </Typography>
                                                        <Typography
                                                          fontSize={12}
                                                          fontWeight={700}
                                                        >
                                                          {newVal}
                                                        </Typography>
                                                      </>
                                                    ) : oldVal ? (
                                                      <Typography
                                                        fontSize={12}
                                                        color="#9ca3af"
                                                        sx={{
                                                          textDecoration:
                                                            "line-through",
                                                        }}
                                                      >
                                                        {oldVal}
                                                      </Typography>
                                                    ) : (
                                                      <Typography
                                                        fontSize={12}
                                                        fontWeight={700}
                                                      >
                                                        {newVal}
                                                      </Typography>
                                                    )}
                                                  </Stack>
                                                );
                                              },
                                            )}
                                          </Box>
                                        </Collapse>
                                      </Box>
                                    );
                                  })}
                              </Box>
                            </Collapse>
                          </Box>
                        )}
                    </Box>
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography fontSize={13} color="#6b7280">
              契約履歴はありません
            </Typography>
          </Box>
        )}
        {/* 無限スクロール sentinel: viewport に入ると onLoadMore 発火 */}
        {pagedHistory.length > 0 && hasMore && (
          <Box
            ref={bottomObserverRef}
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "center",
              minHeight: 32,
            }}
          >
            <CustomLoading
              loading={isLoadingMore}
              fullScreen={false}
              variant="circular"
              size="small"
            />
          </Box>
        )}
      </Box>

      {/* 退去レコードの「契約情報を見る」モーダル */}
      {previousContractorModalRecord && unitId && (
        <PreviousContractorModal
          open
          onClose={() => setPreviousContractorModalRecordId(null)}
          record={previousContractorModalRecord}
          unitId={unitId}
        />
      )}
    </Box>
  );
};

export default ContractHistoryTab;
